import React, { MouseEvent, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { Button, responsive } from '@vp/swan';
import { useIdentityContext } from '@99designs/design-services-common';
import { __ } from '@99designs/i18n';
import { BRIEF_PAGE_NAME, sendExperimentClickTrackingEvent } from '@99designs/tracking';
import { Configuration } from '../../lib/config';
import { useCart } from '../../lib/hooks';
import { getExperimentVariation } from '../Submit/getExperiment';
import { useFieldProcessing } from './FieldProcessingProvider';

export const addToCartEventTracking = {
    label: 'Design Services Added',
    eventDetail: '/design/briefs/create;/c;BriefForm;AddToCart-CTA',
    navigationDetail: 'AddToCart-CTA',
    category: 'Product Added',
};

export interface ButtonProps {
    isWithCart: boolean;
    isEditMode?: boolean;
    label?: string;
    editLabel?: string;
    width?: 'wide' | 'standard' | 'full-width';
    skin?: 'primary' | 'secondary';
    isDisabled?: boolean;
}

const ResponsiveButton = responsive(Button);

function useSubmitBriefCTACopy(
    locale: string,
    briefId: string,
    isWithCart: boolean,
    label?: string,
    editLabel?: string
) {
    const { authorizationHeader, shopperId, isSignedIn } = useIdentityContext();
    const { isInCart } = useCart(locale, { authorizationHeader, shopperId });
    const copy = label || (isWithCart ? __('Add to cart') : __('Submit'));
    const [CTAcopy, setCTAcopy] = useState(copy);

    useEffect(() => {
        async function getCartData() {
            const briefInCart = await isInCart(briefId);
            setCTAcopy(briefInCart ? editLabel || __('Submit Changes') : copy);
        }
        if (isSignedIn && isWithCart) {
            getCartData();
        }
    }, [briefId, isInCart, isSignedIn, editLabel, isWithCart, copy]);

    return CTAcopy;
}

export const SubmitBriefButton = ({
    briefId,
    locale,
    loading,
    label,
    editLabel,
    isWithCart,
}: {
    briefId: string;
    locale: string;
    loading: boolean;
} & ButtonProps) => {
    const CTAcopy = useSubmitBriefCTACopy(locale, briefId, isWithCart, label, editLabel);
    const { processingFields } = useFieldProcessing();
    const isProcessing = processingFields.length > 0;
    return (
        <Button
            data-section={addToCartEventTracking.label}
            data-position={0}
            data-translation={addToCartEventTracking.navigationDetail}
            skin="primary"
            type="submit"
            disabled={loading || isProcessing}
        >
            {CTAcopy}
        </Button>
    );
};

export const SubmitMultiStepBriefButton = ({
    label,
    editLabel,
    isWithCart,
    isEditMode,
    skin = 'primary',
    width = 'standard',
    isDisabled = false,
}: ButtonProps) => {
    let CTAcopy = label || (isWithCart ? __('Add to cart') : __('Submit'));

    if (isWithCart && isEditMode) {
        CTAcopy = editLabel || __('Submit Changes');
    }

    const {
        formState: { errors },
        getValues,
    } = useFormContext();
    const { processingFields } = useFieldProcessing();
    const isProcessing = processingFields.length > 0;
    const hasErrors = Object.keys(errors).length > 0;

    const fireExperiment = async (_: MouseEvent<HTMLButtonElement>) => {
        const experimentName = 'paid_design_services_in_studio_help_panel_2';
        const values = getValues();
        const requestType = values.requestType;
        const isDesignLive = requestType === 'DesignLive';
        const isStandard = requestType === 'Standard';

        let experimentDetail = '';
        if (isDesignLive) {
            experimentDetail = 'expo_2347_Design_Expert_Brief_Submitted_Design_Live';
        } else if (isStandard) {
            experimentDetail = 'expo_2347_Design_Expert_Brief_Submitted_Email_Review';
        }

        const variationName = await getExperimentVariation(experimentName);
        if (variationName === null) return true;

        sendExperimentClickTrackingEvent({
            pageName: BRIEF_PAGE_NAME,
            pageSection: 'AddToCart-CTA',
            pageStage: '',
            experimentDetail,
            experimentName,
            label: 'Design Services Added',
            variationName,
        });

        return true;
    };

    return (
        <ResponsiveButton
            onClick={fireExperiment}
            data-section={addToCartEventTracking.label}
            data-position={0}
            data-translation={addToCartEventTracking.navigationDetail}
            skin={skin}
            width="full-width"
            type="submit"
            disabled={isProcessing || hasErrors || isDisabled}
            sm={{
                width,
            }}
        >
            {CTAcopy}
        </ResponsiveButton>
    );
};
